/**
 * One `inputs.jsonl` row of a raw macro package. Playjector is the only writer.
 * Times are microseconds on the host QPC clock relative to `manifest.clock.originQpcTicks`;
 * normalized coordinates are relative to `manifest.capture.rect`.
 */
import { z } from 'zod';

const base = {
  seq: z.number().int().nonnegative(),
  tUs: z.number().int().nonnegative(),
  source: z.enum(['desktop', 'editor']),
};
const norm = z.number().min(0).max(1);
const axis = z.number().int().min(-32768).max(32767);

export const rawInputEventSchema = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('key'), action: z.enum(['down', 'up']),
    vk: z.number().int().min(1).max(254), scan: z.number().int().min(0).max(0xffff),
    extended: z.boolean(), repeat: z.boolean() }).strict(),
  z.object({ ...base, kind: z.literal('mouse-rel'), dx: z.number().int(), dy: z.number().int() }).strict(),
  z.object({ ...base, kind: z.literal('mouse-abs'), x: norm, y: norm }).strict(),
  z.object({ ...base, kind: z.literal('mouse-button'), action: z.enum(['down', 'up']),
    button: z.enum(['left', 'right', 'middle', 'back', 'forward']), x: norm, y: norm }).strict(),
  z.object({ ...base, kind: z.literal('mouse-wheel'), dx: z.number().int().min(-32767).max(32767),
    dy: z.number().int().min(-32767).max(32767) }).strict(),
  z.object({ ...base, kind: z.literal('touch'), action: z.enum(['down', 'move', 'up']),
    contactId: z.number().int().min(0).max(9), x: norm, y: norm }).strict(),
  z.object({ ...base, kind: z.literal('gamepad'), slot: z.number().int().min(0).max(3),
    buttons: z.number().int().min(0).max(0xffff), lt: z.number().int().min(0).max(255),
    rt: z.number().int().min(0).max(255), lx: axis, ly: axis, rx: axis, ry: axis }).strict(),
  z.object({ ...base, kind: z.literal('marker'), label: z.enum(['scope-enter', 'scope-leave',
    'capture-start', 'capture-stop', 'device-change']) }).strict(),
]);
export type RawInputEvent = z.infer<typeof rawInputEventSchema>;
export type RawInputEventKind = RawInputEvent['kind'];

export interface RawInputStreamIssue { index: number; code: 'schema_invalid' | 'seq_gap' | 'time_regression'; message: string }
export interface RawInputStreamValidation { ok: boolean; events: RawInputEvent[]; issues: RawInputStreamIssue[] }

/** Schema-checks every row, then requires `seq` gapless from 0 and `tUs` non-decreasing. */
export function validateRawInputStream(rows: readonly unknown[]): RawInputStreamValidation {
  const events: RawInputEvent[] = [];
  const issues: RawInputStreamIssue[] = [];
  let previousTimeUs = 0;
  rows.forEach((row, index) => {
    const parsed = rawInputEventSchema.safeParse(row);
    if (!parsed.success) {
      issues.push({ index, code: 'schema_invalid', message: parsed.error.issues[0]?.message ?? 'invalid row' });
      return;
    }
    const event = parsed.data;
    if (event.seq !== index) issues.push({ index, code: 'seq_gap', message: `expected seq ${index}, got ${event.seq}` });
    if (event.tUs < previousTimeUs) issues.push({ index, code: 'time_regression', message: `tUs ${event.tUs} < ${previousTimeUs}` });
    previousTimeUs = Math.max(previousTimeUs, event.tUs);
    events.push(event);
  });
  return { ok: issues.length === 0, events, issues };
}

/** Parses a JSONL document (one JSON value per non-empty line). */
export function parseJsonLines(text: string): unknown[] {
  return text.split('\n').filter(line => line.trim().length > 0).map(line => JSON.parse(line) as unknown);
}
